<script setup lang="ts">
import { useFetchBlockDetail } from '~/api/service/block/[blockNumber]/index.service'
import IconButton from '~/components/button/IconButton.vue'
import BcLabelContainer from '~/ui/LabelContainer/BcLabelContainer.vue'
import { getRouteParam } from '~/utils/route.util'

const blockNumber = computed(() => getRouteParam('blockNumber'))

const { data } = useFetchBlockDetail(blockNumber)

const { copy } = useCopy()
</script>

<template>
  <div v-if="data">
    <div class="bc-container">
      <div class="bc-half">
        <div class="flex items-center">
          <FontAwesomeIcon
            icon="cube"
            size="16px"
          />
          <span class="ml-1 text-lg/[1.25rem] font-bold">Block Number</span>
        </div>
        <div class="mt-2 flex items-center">
          <span class="text-3xl/[2rem] font-bold"> {{ blockNumber }} </span>
          <div class="ml-4 flex gap-2">
            <BcIconLink
              v-tooltip="'View previous block'"
              :to="routes.getBlockDetailPageWithTab((parseInt(data.BNO) - 1).toString(), blockDetailPageType.block)"
              icon="chevron-left"
            />
            <BcIconLink
              v-tooltip="'View next block'"
              :to="routes.getBlockDetailPageWithTab((parseInt(data.BNO) + 1).toString(), blockDetailPageType.block)"
              icon="chevron-right"
            />
          </div>
        </div>
        <div class="my-4 flex flex-col">
          <span class="bc-value"> {{ data.AG }}</span>
          <time class="mt-2 text-base/[0.75rem] font-normal text-primary/40">({{ data.unixTimeStamp }})</time>
        </div>
      </div>
      <div class="bc-half">
        <div class="bc-detail">
          <BcLabelContainer
            class="bc-label"
            label="Block Reward"
          >
            <template #content>
              <div class="bc-content">
                <span class="bc-value">{{ data.blockReward }}<span class="bc-unit">{{ UNIT }}</span></span>
              </div>
            </template>
          </BcLabelContainer>
          <BcLabelContainer
            class="bc-label"
            label="Transactions"
          >
            <template #content>
              <div class="bc-content">
                <span class="bc-value">{{ data.TCNTINFO.TRXCNT }}<span class="bc-unit">({{ data.TCNTINFO.CINCNT }} internal)</span></span>
              </div>
            </template>
          </BcLabelContainer>
          <BcLabelContainer
            class="bc-label"
            label="Validator"
          >
            <template #content>
              <div class="bc-content">
                <span class="bc-value">{{ data.VD }} </span>
                <IconButton
                  class="ml-2"
                  icon="copy"
                  size="xl"
                  @click="copy(data.VD)"
                />
              </div>
            </template>
          </BcLabelContainer>
          <BcLabelContainer
            class="bc-label"
            label="Gas Used"
          >
            <template #content>
              <div class="bc-content flex-wrap">
                <div class="flex items-center gap-x-1">
                  <span class="text-lg">{{ data.gasUsed }}</span>
                  <Chip :value="`${data.GUP} %`" />
                </div>
                <span class="text-lg text-primary/40">/ {{ data.gasLimit }}</span>
              </div>
            </template>
          </BcLabelContainer>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.bc-label {
  @apply text-base/[1.125rem]
}

.bc-value {
  @apply text-lg/[1.25rem] truncate;

  @include sm {
    @apply whitespace-normal break-all;
  }
}

.bc-unit {
  @apply ml-1 text-primary/40
}

.bc-content {
  @apply flex gap-1 items-center
}

.bc-container {
  @apply flex p-4 text-primary;

  @include sm {
    @apply flex-col;
  }

  .bc-half {
    @apply basis-1/2;

    @include sm {
      @apply basis-full;
    }

    .bc-detail {
      @apply grid h-full grid-cols-2 gap-8;

      @include sm {
        @apply grid-cols-1 gap-4;
      }
    }
  }
}
</style>